import { match } from 'ts-pattern'

import { TASK_STATUS_ORDER } from '@frame/ui-components/direct-task-card/constants'
import { TaskVisualStatus } from '@frame/ui-components/direct-task-card/types'

import { DirectTask, DirectTaskState, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'

import { NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER } from '../constants'

export function isBilStrategy(strategy: TrueworkJsStrategy): boolean {
  return match(strategy)
    .with(TrueworkJsStrategy.CREDENTIALS, () => true)
    .with(TrueworkJsStrategy.DOCUMENTS, () => true)
    .with(TrueworkJsStrategy.BANK, () => true)
    .with(TrueworkJsStrategy.EMPLOYMENT_STRATEGIES, () => false)
    .with(TrueworkJsStrategy.INSTANT, () => false)
    .with(TrueworkJsStrategy.SMART_OUTREACH, () => false)
    .with(TrueworkJsStrategy.IRS, () => false)
    .exhaustive()
}

export function isNotBank(strategy: TrueworkJsStrategy): boolean {
  return strategy !== TrueworkJsStrategy.BANK
}

export function isNotDocuments(strategy: TrueworkJsStrategy): boolean {
  return strategy !== TrueworkJsStrategy.DOCUMENTS
}

export function isVerifiedTask(directTask: DirectTask) {
  let verified = false

  if (directTask?.report) {
    verified = true
  }

  if ([DirectTaskState.PROCESSING, DirectTaskState.COMPLETE].includes(directTask?.state)) {
    verified = true
  }

  return verified
}

function isCompleteTask(directTask: DirectTask) {
  return directTask.connector_company_identifier === NO_ARGYLE_ITEM_ID_FOUND_IDENTIFIER
}

function isFailedTask(directTask: DirectTask) {
  return directTask.state === DirectTaskState.FAILED
}

function isVerifiedViaCredentials(directTask: DirectTask) {
  return isVerifiedTask(directTask) && directTask.strategy === TrueworkJsStrategy.CREDENTIALS
}

function isVerifiedViaDocuments(directTask: DirectTask) {
  return isVerifiedTask(directTask) && directTask.strategy === TrueworkJsStrategy.DOCUMENTS
}

function isVerifiedViaBank(directTask: DirectTask) {
  return isVerifiedTask(directTask) && directTask.strategy === TrueworkJsStrategy.BANK
}

export function isVerifiedViaInstant(directTask: DirectTask) {
  return isVerifiedTask(directTask) && directTask.strategy === TrueworkJsStrategy.INSTANT
}

function isVerifiedViaTax(directTask: DirectTask) {
  return isVerifiedTask(directTask) && directTask.strategy === TrueworkJsStrategy.IRS
}

export function isSkippedTask(directTask: DirectTask) {
  return directTask.state === DirectTaskState.SKIPPED
}

export function isMarkedComplete(directTask: DirectTask) {
  return (
    isVerifiedTask(directTask) || isCompleteTask(directTask) || isSkippedTask(directTask) || isFailedTask(directTask)
  )
}

function getVisualStatusFromDirectTask({
  directTask,
  credentialsEnabled,
  documentsEnabled,
  bankEnabled,
  irsEnabled,
  docsForMortgageEnabled,
}: {
  directTask: DirectTask
  credentialsEnabled: boolean
  documentsEnabled: boolean
  bankEnabled: boolean
  irsEnabled: boolean
  docsForMortgageEnabled?: boolean
}) {
  if (isSkippedTask(directTask)) {
    return TaskVisualStatus.SKIPPED
  }

  if (isVerifiedViaInstant(directTask)) {
    return TaskVisualStatus.COMPLETED_INSTANT
  }

  if (isVerifiedViaCredentials(directTask)) {
    return TaskVisualStatus.COMPLETED_CREDENTIALS
  }

  if (isVerifiedViaDocuments(directTask)) {
    return TaskVisualStatus.COMPLETED_DOCUMENTS
  }

  if (isVerifiedViaBank(directTask)) {
    return TaskVisualStatus.COMPLETED_BANK
  }

  if (isVerifiedViaTax(directTask)) {
    return TaskVisualStatus.COMPLETED_IRS
  }

  if (isFailedTask(directTask)) {
    return TaskVisualStatus.ERROR
  }

  if (isCompleteTask(directTask) || isVerifiedTask(directTask)) {
    return TaskVisualStatus.IN_PROGRESS
  }

  if (bankEnabled && directTask.strategy === TrueworkJsStrategy.BANK) {
    return TaskVisualStatus.NOT_ATTEMPTED_BANK
  }

  if (irsEnabled && directTask.strategy === TrueworkJsStrategy.IRS) {
    return TaskVisualStatus.NOT_ATTEMPTED_IRS
  }

  if (docsForMortgageEnabled) {
    if (!directTask.strategy || directTask.strategy === TrueworkJsStrategy.CREDENTIALS) {
      // We want to show the user that Credentials is their only option at first. If creds fails, then go to documents.
      return TaskVisualStatus.NOT_ATTEMPTED_CREDENTIALS
    }

    if (directTask.strategy === TrueworkJsStrategy.DOCUMENTS) {
      return TaskVisualStatus.NOT_ATTEMPTED_DOCUMENTS
    }
  }

  if (credentialsEnabled && documentsEnabled) {
    return TaskVisualStatus.NOT_ATTEMPTED_CREDENTIALS_DOCUMENTS
  }

  if (documentsEnabled) {
    return TaskVisualStatus.NOT_ATTEMPTED_DOCUMENTS
  }

  return TaskVisualStatus.NOT_ATTEMPTED_CREDENTIALS
}

export function addStatusToTasks({
  directTasks,
  credentialsEnabled,
  documentsEnabled,
  bankEnabled,
  irsEnabled,
  docsForMortgageEnabled,
}: {
  directTasks: DirectTask[]
  credentialsEnabled: boolean
  documentsEnabled: boolean
  bankEnabled: boolean
  irsEnabled: boolean
  docsForMortgageEnabled?: boolean
}) {
  return directTasks
    .map((task, index) => {
      return {
        ...task,
        originalIndex: index,
        visualStatus: getVisualStatusFromDirectTask({
          directTask: task,
          credentialsEnabled,
          documentsEnabled,
          bankEnabled,
          irsEnabled,
          docsForMortgageEnabled,
        }),
      }
    })
    .sort((a, b) => {
      const statusOrderDifference =
        TASK_STATUS_ORDER[a.visualStatus as keyof typeof TASK_STATUS_ORDER] -
        TASK_STATUS_ORDER[b.visualStatus as keyof typeof TASK_STATUS_ORDER]

      if (statusOrderDifference === 0) {
        return a.originalIndex - b.originalIndex
      }

      return statusOrderDifference
    })
}

export function isSkippable(directTask: DirectTask) {
  // The literal "skip" button is temporarily disabled.
  return (
    false &&
    // If an employment has a connector company identifier, that means the target has selected to verify the employment at least once
    // If there is not a connector company identifier, the employment has not been attempted, so no need to show the "skip" CTA
    directTask?.connector_company_identifier &&
    directTask.state !== DirectTaskState.SKIPPED &&
    directTask.state !== DirectTaskState.FAILED
  )
}
